import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Chip, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { useMotivationRows } from '../../hooks/useMotivationRows'
import { useUAInsightsMeta } from '../../hooks/useUAInsightsMeta'
import { useUAInsightsActions } from '../../hooks/useUAInsightsNavigation'
import { SecondData } from '../../types'

interface SecondDetailPanelProps {
  secondData: SecondData
}

interface MotivationChip {
  /** Motivation `code`, the key of the per-second `intensity` map. */
  key: string
  /** Absent for a code `/meta` doesn't know; the chip is then not clickable. */
  id?: string
  label: string
}

const motivationChipClickProps = (chip: MotivationChip, openMotivation: (motivationId: string) => void) => {
  const { id } = chip
  return id ? { clickable: true, onClick: () => openMotivation(id) } : {}
}

export const SecondDetailPanel: FC<SecondDetailPanelProps> = ({ secondData }) => {
  const { t } = useTranslation('ua-insights')
  const { openMotivation } = useUAInsightsActions()
  const { motivationByCode } = useUAInsightsMeta()
  const secondsForRows = useMemo(() => [secondData], [secondData])
  const { motivationKeysInOrder, motivationKeyToLabel } = useMotivationRows(secondsForRows)

  const { primaryMotivations, otherMotivations } = useMemo(() => {
    // Hide motivations at the floor intensity of 1 — those are "barely
    // present" signals that add noise to the per-second chip list.
    const ranked = motivationKeysInOrder
      .map((key) => ({
        key,
        id: motivationByCode(key)?.id,
        label: motivationKeyToLabel[key] ?? key,
        intensity: secondData.intensity[key] ?? 0,
      }))
      .filter((entry) => entry.intensity > 1)

    if (ranked.length === 0) {
      return { primaryMotivations: [] as MotivationChip[], otherMotivations: [] as MotivationChip[] }
    }

    const maxIntensity = Math.max(...ranked.map((entry) => entry.intensity))
    const primary = ranked.filter((entry) => entry.intensity === maxIntensity)
    const other = ranked.filter((entry) => entry.intensity < maxIntensity).sort((a, b) => b.intensity - a.intensity)

    return { primaryMotivations: primary, otherMotivations: other }
  }, [secondData.intensity, motivationKeysInOrder, motivationKeyToLabel, motivationByCode])

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: 'action.hover',
        borderRadius: borderRadiusDefault,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          {secondData.second}s
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {secondData.description}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 0 }}>
        {primaryMotivations.length > 0 ? (
          primaryMotivations.map((m) => <Chip key={m.key} label={m.label} size="small" color="primary" {...motivationChipClickProps(m, openMotivation)} />)
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('common:not_available_shorthand')}
          </Typography>
        )}
        {otherMotivations.map((m) => (
          <Chip key={m.key} label={m.label} size="small" variant="outlined" {...motivationChipClickProps(m, openMotivation)} />
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {secondData.motivational.reasoning}
      </Typography>
    </Box>
  )
}
